/**
 * Row shaping for the OMS order queue.
 *
 * The queue answers one operational question: who pulls what, from where.
 * Marketplace and location are attributes of a row, not a hierarchy — the
 * floor sorts and filters by them rather than navigating into them.
 *
 * Everything here is pure. The view fetches; this decides.
 */

import { parseLocationFromSKU } from '@/utils/skuLocationParser';
import { normalizeTagBarcode } from '@/utils/tagBarcode';
import { tagSwatch } from '@/utils/tagColors';
import { displayName } from '@/utils/accountLabel';
import { labelItemsByOrder } from '@/utils/labelPrint';
import { EMPTY_SETUP, siteBadge } from '@/utils/locationSites';

/**
 * Why a row cannot simply be pulled off a shelf.
 * Each of these is a real production state, not a defensive branch.
 */
export const ATTENTION = {
  // The sale did not resolve to an inventory item, so we do not know what sold.
  UNMATCHED: 'unmatched',
  // Two sources disagree about what this line is. Resolve before touching stock.
  CONFLICT: 'conflict',
  // One inventory item claimed by more than one sale. A real double-sell.
  //
  // Note this is NOT "the item is marked Sold". Our own sold-detection marks
  // the item Sold within seconds of the normalizer projecting the order
  // (measured: 1.3s apart on order 09-15087-59844), so that status is true of
  // almost every order and alarms on nothing.
  DOUBLE_SELL: 'double_sell',
  // Matched, but neither the location field nor the SKU says where it lives.
  NO_LOCATION: 'no_location',
  // A cancelled sale awaiting a person's decision. Rendered ONLY on the
  // desktop Cancellations page; the queue and the mobile deck never show it.
  CANCELLED: 'cancelled',
};

/**
 * Channels whose sales are fulfilled by pulling stock.
 *
 * ConsignCloud is deliberately absent. It is a real sale source and belongs in
 * the queue as revenue, but every ConsignCloud event is a point-of-sale ring-up
 * at the register — the customer already walked out with the item. Verified in
 * production: all 57 ConsignCloud orders projected as `closed`.
 *
 * This is a property of the channel, not of the status. Keying it on status
 * alone would silently start generating pull tasks if ConsignCloud ever emitted
 * a non-closed event.
 */
// Depop belongs here and ConsignCloud does not, for the reason above: a
// ConsignCloud event is a register ring-up where the customer already left
// with the item, while a Depop sale ships -- the receipt carries a parcel, a
// prepaid label and a shipping_deadline. Without this a Depop order would
// project correctly and then never become pickable.
// A Shopify order ships from the seller (the merchant fulfils it), so it must
// become pickable.
export const FULFILLABLE_CHANNELS = ['ebay', 'poshmark', 'medusa', 'depop', 'shopify'];

export function isFulfillable(channel) {
  return FULFILLABLE_CHANNELS.indexOf(channel) !== -1;
}

/**
 * Order statuses that still need someone to do something. Everything else is
 * history: shipped, delivered, closed, cancelled and returned are all done.
 */
export const PICKABLE_STATUSES = ['pending', 'ready_to_ship', 'problem'];

/** A row is pickable only if the channel is fulfilled AND the sale is open. */
export function needsPicking(row) {
  if (!row) return false;
  return isFulfillable(row.channel) && PICKABLE_STATUSES.indexOf(row.orderStatus) !== -1;
}

/**
 * A marketplace filter entry is an ACCOUNT, not a channel.
 *
 * There can be more than one account on the same marketplace (two Poshmark
 * closets, two eBay sellers), and "Poshmark" alone cannot tell them apart.
 * Orders projected before Order.channelAccountRef existed carry null, which
 * degrades to the bare channel rather than vanishing from the filter.
 */
export function accountKey(row) {
  return (row && row.channel) + '::' + ((row && row.channelAccountRef) || '');
}

/**
 * Canonicalise a location label so the same shelf sorts and filters as one.
 *
 * Production carries real variants of the same place: 'In-store' vs 'In-Store'
 * (4582 vs 54 rows), 'HOR #7' vs 'HOR#10'. It also carries junk like '.' (34
 * rows), which is not a location and must not become a bucket.
 *
 * @param {String} raw
 * @returns {String|null} display label, or null when there is no location
 */
export function normalizeLocation(raw) {
  if (!raw || typeof raw !== 'string') return null;

  const label = raw
    .trim()
    .replace(/\s+/g, ' ')      // collapse runs, keep words readable
    .replace(/\s*#\s*/g, '#')  // 'HOR #7' and 'HOR#7' are one rack
    .toUpperCase();

  // A label with no letters or digits is punctuation, not a place.
  if (!/[A-Z0-9]/.test(label)) return null;

  return label;
}

/**
 * Where does this item physically live?
 *
 * The location field is authoritative when set. When it is not, the SKU often
 * encodes the rack — that fallback covers 21.8% of published inventory, so
 * skipping it would strand roughly one pull in five.
 */
export function resolveLocation(inventory, line) {
  const fromField = normalizeLocation(inventory && inventory.location);
  if (fromField) return { location: fromField, source: 'field' };

  const sku = (inventory && inventory.sku) || (line && line.sku);
  const fromSku = normalizeLocation(parseLocationFromSKU(sku));
  if (fromSku) return { location: fromSku, source: 'sku' };

  return { location: null, source: null };
}

/**
 * Flatten one sold line into the row a picker reads.
 *
 * @param {Object} order      canonical Order
 * @param {Object} line       canonical OrderLine
 * @param {Object} match      canonical OrderMatch (may be null)
 * @param {Object} inventory  live Inventory item, or null when unmatched
 */
export function pickListRowFor(order, line, match, inventory, setup = EMPTY_SETUP) {
  const state = (match && match.state) || 'unmatched';

  // An unresolved line has no location by definition — we do not know the item.
  const resolved = state === 'matched'
    ? resolveLocation(inventory, line)
    : { location: null, source: null };

  let attention = null;
  if (state === 'conflict') {
    attention = ATTENTION.CONFLICT;
  } else if (state !== 'matched') {
    attention = ATTENTION.UNMATCHED;
  } else if (!resolved.location) {
    attention = ATTENTION.NO_LOCATION;
  }

  return {
    orderId: order.id,
    externalOrderId: order.externalOrderId,
    channel: order.channel,
    channelAccountRef: order.channelAccountRef || null,
    orderStatus: order.status,
    orderTotalCents: typeof order.totalCents === 'number' ? order.totalCents : null,
    // What the seller keeps, as the marketplace reported it: the number to
    // write down. ORDER-level -- a bundle has one figure for all its items.
    netProceedsCents: typeof order.netProceedsCents === 'number' ? order.netProceedsCents : null,
    // Why the status is what it is. Null when the status is ordinary.
    statusReason: order.statusReason || null,
    // Prepaid label, when the marketplace issues one. Poshmark always does;
    // eBay never does, so those need a label bought elsewhere.
    shippingLabelUrl: order.shippingLabelUrl || null,
    // A label Sellary re-hosted (Depop). Downloaded through an owner-checked,
    // short-lived link minted from this key -- never linked directly -- so it
    // wins over shippingLabelUrl wherever both exist.
    shippingLabelKey: order.shippingLabelKey || null,
    channelNativeStatus: order.channelNativeStatus || null,
    placedAt: order.placedAt || null,

    lineId: line.id,
    // Line snapshot FIRST, matched inventory as the fallback. The line is what
    // the channel actually reported and stays the honest record; the fallback
    // exists because a channel can report a sale without reporting what sold.
    // Depop is the live case: its receipt carries no sku field anywhere, so
    // an order rendered a row with no sku, no title and -- because
    // inventoryHref() is built from row.sku -- a dead "open in inventory"
    // link. The inventory row is already loaded here for images and location
    // (INVENTORY_FIELDS selects sku and title), and it is authoritative:
    // unlike a snapshot it cannot go stale after a SKU renumber.
    sku: line.sku || (inventory && inventory.sku) || null,
    title: line.titleSnapshot || (inventory && inventory.title) || null,
    quantity: typeof line.quantity === 'number' ? line.quantity : 1,
    salePriceCents: typeof line.salePriceCents === 'number' ? line.salePriceCents : null,
    listPriceCents: typeof line.listPriceCents === 'number' ? line.listPriceCents : null,
    priceBasis: line.priceBasis || null,

    matchState: state,
    matchMethod: (match && match.method) || null,
    inventoryId: (match && match.inventoryId) || null,
    // OPT-447: the matcher's candidate ids (ambiguous/conflict), and whether an
    // OrderMatch row exists at all (linking updates it, so it must).
    candidates: (match && Array.isArray(match.candidates) ? match.candidates : []).filter(Boolean),
    hasMatchRecord: !!match,
    matchedInventoryStatus: (match && match.matchedInventoryStatus) || null,

    // OPT-455: the item's price-tag color (canonical name) or null. Matched
    // lines only, like location: an unresolved line has no known item.
    tagColor: state === 'matched' ? ((tagSwatch(inventory && inventory.tagColor) || {}).name || null) : null,
    location: resolved.location,
    locationSource: resolved.source,
    // OPT-295: the tenant's site for this location ({ kind, label }), or null when it has none set up.
    site: siteBadge(resolved.location, setup),
    images: (inventory && Array.isArray(inventory.images) ? inventory.images : []).filter(Boolean),
    // The item's own tag, drawn on the phone deck for a scanner. Only the
    // store's tag format counts; anything else is null and no barcode shows.
    barcode: normalizeTagBarcode(inventory && inventory.barcode),

    attention,
    // Set by preparePickList, which can see the other rows.
    fulfillable: isFulfillable(order.channel),
  };
}

/**
 * The row as the normalizer will project it once a person has linked
 * `inventory` to `line` (OPT-447): a manual match. Built through
 * pickListRowFor so the attention chip, location, site and barcode come out
 * exactly as they will after a reload.
 */
export function linkedRow(order, line, inventory, setup = EMPTY_SETUP) {
  return pickListRowFor(order, line, {
    id: line.id,
    orderLineId: line.id,
    inventoryId: inventory.id,
    state: 'matched',
    method: 'manual',
    candidates: [],
    matchedInventoryStatus: null,
  }, inventory, setup);
}

/**
 * Flag every inventory item claimed by more than one sold line.
 *
 * This is the only reliable double-sell signal available here: a per-row check
 * cannot see the other sale. Runs before filtering so both sides get flagged.
 */
function flagDoubleSells(rows) {
  const claims = new Map();
  for (const row of rows) {
    if (!row.inventoryId) continue;
    if (!claims.has(row.inventoryId)) claims.set(row.inventoryId, []);
    claims.get(row.inventoryId).push(row);
  }

  return rows.map((row) => {
    const claimed = row.inventoryId ? claims.get(row.inventoryId) : null;
    if (!claimed || claimed.length < 2) return row;
    // A double-sell outranks a missing location: two customers bought one item.
    if (row.attention && row.attention !== ATTENTION.NO_LOCATION) return row;
    return { ...row, attention: ATTENTION.DOUBLE_SELL };
  });
}

/**
 * Build the flat row list the table renders.
 *
 * Double-sell flagging happens across ALL rows before any filter, so hiding a
 * marketplace can never hide the fact that one of its sales collides with a
 * sale on another marketplace.
 *
 * @param {Array} rows           output of pickListRowFor
 * @param {Object} [opts]
 * @param {Array}  [opts.accounts] accountKey values to show; omit for all
 */
export function preparePickList(rows, opts = {}) {
  const out = flagDoubleSells(rows || []);

  if (Array.isArray(opts.accounts)) {
    const allowed = new Set(opts.accounts);
    return out.filter((r) => allowed.has(accountKey(r)));
  }

  return out;
}

/**
 * Lay rows out so an order's items stay together.
 *
 * A Poshmark bundle is several items in ONE parcel. A flat list gives a picker
 * no way to know that, so a multi-item order gets a header row and its items
 * follow it. Single-item orders stay as plain rows -- a header for every order
 * would be pure noise, since most orders are one item.
 *
 * Grouping is STABLE against the caller's sort: an order takes the position of
 * its first row in the incoming order, and its siblings are pulled up to it.
 * So sorting by location still decides which order comes first, without
 * scattering a bundle across the table.
 *
 * @param {Array} rows rows in the order the table wants to render them
 * @returns {Array} [{kind:'order', ...}|{kind:'item', row, grouped:Boolean}]
 */
export function groupRowsByOrder(rows) {
  const list = rows || [];
  const byOrder = new Map();
  for (const r of list) {
    if (!byOrder.has(r.orderId)) byOrder.set(r.orderId, []);
    byOrder.get(r.orderId).push(r);
  }

  const out = [];
  const done = new Set();
  for (const r of list) {
    if (done.has(r.orderId)) continue;
    const siblings = byOrder.get(r.orderId);
    if (siblings.length < 2) {
      out.push({ kind: 'item', row: r, grouped: false });
      done.add(r.orderId);
      continue;
    }
    done.add(r.orderId);
    out.push({
      kind: 'order',
      orderId: r.orderId,
      externalOrderId: r.externalOrderId,
      channel: r.channel,
      channelAccountRef: r.channelAccountRef,
      orderStatus: r.orderStatus,
      statusReason: r.statusReason,
      // One parcel, so one label for the whole bundle.
      shippingLabelUrl: r.shippingLabelUrl,
      shippingLabelKey: r.shippingLabelKey,
      placedAt: r.placedAt,
      itemCount: siblings.length,
      // The order total, not the sum of the lines: a Poshmark bundle is
      // discounted, so two $40 items really do total $68.
      orderTotalCents: r.orderTotalCents,
      netProceedsCents: r.netProceedsCents,
      // Null when any line has no price: summing the priced ones would show
      // a partial total that reads like the whole.
      lineTotalCents: siblings.some((x) => x.salePriceCents === null)
        ? null
        : siblings.reduce((s, x) => s + x.salePriceCents, 0),
      rows: siblings,
    });
    // `last` marks where the parcel ends, so the table can close it off.
    siblings.forEach((sib, i) => out.push({ kind: 'item', row: sib, grouped: true, last: i === siblings.length - 1 }));
  }
  return out;
}

/**
 * Every order status the derivation can produce.
 *
 * This exists so `reconcileFilters` can tell "a status this user has never been
 * offered" from "a status the vocabulary does not contain". Seeding the known
 * set with observed statuses only means the first `shipped` order to arrive
 * auto-selects itself, which silently rebuilds the pile of done work the
 * default is there to remove.
 *
 * Kept in sync with tools/oms/status.cjs by a test rather than by an import:
 * that file is CommonJS and lives outside src/, and pulling it into the browser
 * bundle to read two arrays is not worth the build coupling.
 */
export const CANONICAL_STATUSES = [
  'pending',
  'ready_to_ship',
  'shipped',
  'delivered',
  'closed',
  'cancelled',
  'returned',
  'problem',
];

/**
 * What the queue selects when nobody has chosen.
 *
 * The queue is a work list, so it defaults to work: exactly the statuses
 * needsPicking() acts on. `pending` belongs here -- an unpaid order is still
 * something a person has to watch.
 */
export const DEFAULT_STATUSES = PICKABLE_STATUSES.slice();

/** Pick states. Absence of a pick means "not picked yet"; there is no row. */
export const PICK_STATES = { PICKED: 'picked', NOT_FOUND: 'not_found' };

/**
 * Free-text search over the fields the search box advertises.
 *
 * v-data-table used to do this internally, over every field of the row. The
 * deck has no table to borrow it from, and both views have to agree on what a
 * search means, so it moves here and gets scoped to the fields the label names.
 */
export function searchRows(rows, term, channelLabel = (c) => c) {
  const q = String(term || '').trim().toLowerCase();
  if (!q) return rows || [];
  return (rows || []).filter((r) => {
    const hay = [
      r.sku,
      r.title,
      r.location,
      r.externalOrderId,
      channelLabel(r.channel),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return hay.indexOf(q) !== -1;
  });
}

const SORT_READERS = {
  placedAt: (r) => (r.placedAt ? new Date(r.placedAt).getTime() : 0),
  sku: (r) => (r.sku || '').toLowerCase(),
  title: (r) => (r.title || '').toLowerCase(),
  location: (r) => (r.location || '').toLowerCase(),
  channel: (r) => (r.channel || '').toLowerCase(),
  salePriceCents: (r) => (typeof r.salePriceCents === 'number' ? r.salePriceCents : -1),
  netProceedsCents: (r) => (typeof r.netProceedsCents === 'number' ? r.netProceedsCents : -1),
  orderStatus: (r) => (r.orderStatus || '').toLowerCase(),
};

/**
 * Sort rows for either view.
 *
 * Stable: equal keys keep their incoming order, so grouping downstream stays
 * predictable and a re-sort on the same key does not shuffle the deck under
 * someone's thumb.
 */
export function sortRows(rows, sortBy, desc) {
  const read = SORT_READERS[sortBy];
  if (!read) return (rows || []).slice();
  const dir = desc ? -1 : 1;
  return (rows || [])
    .map((row, i) => ({ row, i }))
    .sort((a, b) => {
      const av = read(a.row);
      const bv = read(b.row);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return a.i - b.i;
    })
    .map((x) => x.row);
}

/**
 * One card per line item, with the bundle facts the card needs.
 *
 * Deliberately NOT groupRowsByOrder. That emits synthetic `kind:'order'` header
 * entries for the table to render as a spanning row; feeding those to a swipe
 * deck would produce cards that are not items and make "3 of 24" count things
 * you cannot pick. Here every entry is exactly one pickable line, and the
 * parcel facts ride along on it.
 *
 * Siblings of a multi-item order are still drawn together, taking the position
 * of the first sibling in the incoming sort. So a location sort still decides
 * which parcel comes first without scattering a bundle -- at the cost that one
 * sibling can sit away from its own sorted position. The sort menu says so.
 */
export function deckItemsFor(rows, allRows = null) {
  const list = rows || [];
  const byOrder = new Map();
  for (const r of list) {
    if (!byOrder.has(r.orderId)) byOrder.set(r.orderId, []);
    byOrder.get(r.orderId).push(r);
  }
  // The whole parcel, from the unfiltered rows when given: picking mode drops
  // picked lines and a search can match part of a parcel, but the card's
  // bundle band still has to say what it ships with.
  const parcels = new Map();
  for (const r of allRows || list) {
    if (!parcels.has(r.orderId)) parcels.set(r.orderId, []);
    parcels.get(r.orderId).push(r);
  }
  // The label's SKU strip: gathered exactly as the bulk print gathers it.
  const labelItems = labelItemsByOrder(allRows || list);

  const out = [];
  const done = new Set();
  for (const r of list) {
    if (done.has(r.orderId)) continue;
    done.add(r.orderId);
    const siblings = byOrder.get(r.orderId);
    for (const sib of siblings) {
      // An unfiltered list might not hold this row object itself: match by line.
      const parcel = parcels.get(r.orderId) || [];
      const whole = parcel.some((x) => x.lineId === sib.lineId) ? parcel : siblings;
      out.push({
        row: sib,
        // Visible siblings only (the phone list's "ships with N").
        siblingCount: siblings.length - 1,
        // The phone card's bundle band: "item N of M · ships with <these>",
        // over the whole parcel.
        parcelSize: whole.length,
        siblings: whole.filter((x) => x.lineId !== sib.lineId).map((x) => x.sku || '—'),
        bundleIndex: whole.findIndex((x) => x.lineId === sib.lineId) + 1,
        // A bundle ships in one parcel, so every card in the group offers the
        // same label rather than only the first line carrying it.
        parcelLabelUrl: siblings.length > 1 ? r.shippingLabelUrl : sib.shippingLabelUrl,
        parcelLabelKey: siblings.length > 1 ? r.shippingLabelKey : sib.shippingLabelKey,
        // The label belongs to the ORDER; the download query takes its id.
        parcelLabelOrderId: r.orderId,
        // Every SKU the parcel ships, for that label's SKU strip.
        parcelLabelItems: labelItems.get(r.orderId) || [],
      });
    }
  }
  return out;
}

/**
 * Sort choices for the queue.
 *
 * `text` is the full sentence, shown in the menu. `short` is what the mobile
 * toolbar button shows, and it is deliberately tiny: a v-btn is
 * white-space: nowrap and will not shrink, so a long label here pushes the
 * toolbar past a narrow viewport. That made the page scroll sideways on a
 * 412px screen, and a sideways-scrolling page eats the deck's swipe, because
 * native scrolling beats a touch handler.
 */
export const SORT_OPTIONS = [
  // The phone's sort chip. Only the orders a picker walks by: shelf location
  // and SKU, each both ways (the desktop table sorts by any column header).
  { text: 'Location A–Z', short: 'Loc A–Z', by: 'location', desc: false },
  { text: 'Location Z–A', short: 'Loc Z–A', by: 'location', desc: true },
  { text: 'SKU A–Z', short: 'SKU A–Z', by: 'sku', desc: false },
  { text: 'SKU Z–A', short: 'SKU Z–A', by: 'sku', desc: true },
];

/** The longest `short` the mobile toolbar can show without risking overflow. */
export const SORT_SHORT_MAX = 8;

/**
 * Every account the queue's filter should offer.
 *
 * Built from the marketplace accounts FIRST, not from the loaded rows. A
 * row-derived list can only show channels that have already sold something, so
 * a newly connected marketplace — or one whose detection has stopped working —
 * is missing from the filter in exactly the situation where you most need to
 * see it. Medusa sat in that blind spot: zero orders, so nothing distinguished
 * "no Medusa sales this month" from "Medusa is not being polled at all".
 *
 * Rows still contribute, because not every channel is a MarketplaceAccount.
 * ConsignCloud arrives through a connector and has no row in that table, so a
 * list built purely from accounts would drop it — trading one missing channel
 * for another.
 *
 * @param {Array}  rows          loaded pick-list rows
 * @param {Object} accountsById  MarketplaceAccount records keyed by id
 * @param {Function} [channelLabel] channel -> display label
 */
export function accountOptionsFor(rows, accountsById, channelLabel = (c) => c) {
  const byId = accountsById || {};
  const seen = new Map();

  const add = (channel, ref, username) => {
    const key = channel + '::' + (ref || '');
    if (seen.has(key)) return;
    const name = displayName(username);
    seen.set(key, {
      value: key,
      channel,
      channelLabel: channelLabel(channel),
      accountLabel: name || '(account not recorded)',
      text: channelLabel(channel) + ' ' + name,
    });
  };

  // MarketplaceAccount.type is capitalised ("eBay", "Medusa"); every channel
  // string in the OMS is lowercase. Mapping the wrong way round is how a
  // connector lookup silently matches nothing.
  for (const acct of Object.values(byId)) {
    if (!acct || !acct.type) continue;
    add(String(acct.type).toLowerCase(), acct.id, acct.username);
  }

  // Accounts win on key collision, so they supply the username; rows only add
  // what the account list does not already cover.
  for (const r of rows || []) {
    if (!r || !r.channel) continue;
    const acct = byId[r.channelAccountRef];
    add(r.channel, r.channelAccountRef, acct ? acct.username : null);
  }

  return [...seen.values()].sort((a, b) => a.text.localeCompare(b.text));
}

// ---------------------------------------------------------------------------------------------------
// OPT-318: labels bought in Sellary through Shippo. The queue joins the caller's ShippingLabelOut rows
// (myShippingLabels) to its rows by orderId. Everything here is pure so the cell, the bulk button and the
// default work list share one decision.

/** The channels whose orders Sellary can buy a label for. Depop and Poshmark supply their own. */
export const LABEL_BUY_CHANNELS = ['ebay', 'medusa'];
const IN_PROGRESS_MS = 5 * 60 * 1000;
const NO_LABEL_REASONS = {
  depop: 'This marketplace supplies its own label',
  poshmark: 'This marketplace supplies its own label',
};

/** { [orderId]: ShippingLabelOut } */
export function indexLabelsByOrder(labels) {
  const out = {};
  for (const l of labels || []) if (l && l.orderId) out[l.orderId] = l;
  return out;
}

/**
 * What the Label cell shows. `label` is the order's ShippingLabelOut or null; `availability` is
 * LabelsAvailability or null while it loads. { state, reason, hasLabel, warning, refund }.
 *
 *   supplied | create | unavailable | in_progress | unknown | failed | label | finish | send_failed |
 *   voiding | voided | cancelled_with_label
 */
export function labelCellState(row, label, availability, now = Date.now()) {
  const r = row || {};
  const out = (state, extra = {}) => ({ state, reason: null, hasLabel: false, warning: null, refund: null, ...extra });
  if (label) {
    const keysMatch = !!label.labelKey && r.shippingLabelKey === label.labelKey;
    if (label.status === 'purchasing') {
      const age = now - Date.parse(label.createdAt || label.updatedAt || 0);
      return out(age >= IN_PROGRESS_MS ? 'unknown' : 'in_progress');
    }
    if (label.status === 'voiding') return out('voiding');
    if (label.status === 'failed') return out('failed', { reason: label.error || 'The label could not be bought' });
    if (label.status === 'voided') {
      if (label.storeCleanup === 'pending') return out('finish', { reason: 'The store order still shows the voided label' });
      let refund = null;
      if (label.refundStatus === 'ERROR') refund = 'Shippo refused the refund';
      else if (label.refundedCents != null && label.refundedCents > 0) refund = 'Refunded $' + (label.refundedCents / 100).toFixed(2);
      else if (label.billingMode === 'managed') refund = 'Refund pending';
      return out('voided', { refund });
    }
    if (label.status === 'purchased') {
      if (r.orderStatus === 'cancelled') return out('cancelled_with_label', { hasLabel: keysMatch, warning: 'Void this label' });
      if (!keysMatch) return out('finish', { reason: label.error || 'The label is not on the order yet' });
      if (label.sendRetryable) {
        return out('send_failed', { hasLabel: true, warning: 'Tracking not sent to ' + (label.channel === 'ebay' ? 'eBay' : 'the store'), reason: label.sendError || null });
      }
      return out('label', { hasLabel: true });
    }
  }
  if (r.shippingLabelKey || r.shippingLabelUrl) return out('supplied', { hasLabel: true });
  const open = PICKABLE_STATUSES.indexOf(r.orderStatus) !== -1;
  if (NO_LABEL_REASONS[r.channel]) return out('unavailable', { reason: NO_LABEL_REASONS[r.channel] });
  if (LABEL_BUY_CHANNELS.indexOf(r.channel) === -1) return out('unavailable', { reason: 'This marketplace does not supply a label' });
  if (!availability) return out('unavailable', { reason: 'Checking label availability' });
  if (availability.channels.indexOf(r.channel) === -1) {
    return out('unavailable', { reason: availability.reason || (r.channel === 'medusa' ? 'Store labels are not available' : 'Labels are not available') });
  }
  if (!open) return out('unavailable', { reason: 'The order is no longer open' });
  return out('create');
}

/** States in which Create label may start a purchase. */
export function canCreateLabel(state) {
  return state === 'create' || state === 'failed' || state === 'voided';
}

/** The default work list keeps an unfinished Sellary shipment even when the order status says done (S6). */
export function inDefaultWorkList(row, label) {
  return !!row && !!label && label.status === 'purchased' && label.trackStatus !== 'DELIVERED';
}

/**
 * The tracking pill on the row and the phone card: { text, tone, title } or null. Exception and Returned use
 * the alert tone. Nothing is shown before the first scan or for a voided label.
 */
export function trackingPill(label) {
  if (!label || label.status !== 'purchased' || !label.trackStatus) return null;
  const when = label.trackStatusAt ? new Date(label.trackStatusAt) : null;
  const day = (d) => (d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
  const detail = [label.trackDetail, when ? day(when) : ''].filter(Boolean).join(' · ');
  switch (label.trackStatus) {
    case 'TRANSIT': {
      const eta = label.trackEta ? 'Expected ' + day(new Date(label.trackEta)) : '';
      return { text: 'In transit', tone: 'ready', title: [detail, eta].filter(Boolean).join(' · ') };
    }
    case 'DELIVERED':
      return { text: 'Delivered' + (label.deliveredAt || label.trackStatusAt ? ' ' + day(new Date(label.deliveredAt || label.trackStatusAt)) : ''), tone: 'neutral', title: detail };
    case 'FAILURE':
      return { text: 'Exception', tone: 'error', title: detail };
    case 'RETURNED':
      return { text: 'Returned', tone: 'error', title: detail };
    default:
      return null;
  }
}
